// Waiting for a background deep analysis (2026-10-24). The backend runs the analysis AFTER answering /chat
// (deep_analysis_pending) and saves the reply to chat history under the EXACT message the client sent
// ("נתח לעומק את הריצה שלי מהיום (YYYY-MM-DD, X ק"מ) — …", built by /chat/analyze-workout for one claimed workout).
// That message text + a freshness window is the identifier: an unrelated chat message can never complete the wait.
// No claim / job id reaches the client, so a second deep analysis of the SAME workout with the same text inside the
// window would also match — impossible today (one analysis per workout; a retry only after a failed attempt).
//
// The wait is durable (sessionStorage), so leaving Coach and coming back resumes it — reading history only, never a
// new /chat request or AI call. A wait that ends without the reply proves nothing about the backend job.

export const WAIT_MAX_MS = 8 * 60 * 1000;        // poll for up to ~8 minutes from the send
export const MARKER_TTL_MS = 10 * 60 * 1000;     // a marker older than this is dropped (expired)
export const REQUEST_TIMEOUT_MS = 20 * 1000;     // one history request may never hang the wait
export const FRESH_MARGIN_MS = 2 * 60 * 1000;    // clock skew between the phone and the server

const key = (uid) => `deepAnalysisWait_${uid}`;

/** Store the wait for the message just sent (after the backend confirmed deep_analysis_pending). */
export function saveWait(uid, message, sentAt = Date.now(), storage = window.sessionStorage) {
  if (!uid || !message) return null;
  const w = { message: String(message), sentAt: Number(sentAt) };
  try { storage.setItem(key(uid), JSON.stringify(w)); } catch { /* storage blocked: in-memory wait only */ }
  return w;
}

/** The live wait, or null (none / unreadable / expired — an expired marker is removed). */
export function loadWait(uid, now = Date.now(), storage = window.sessionStorage) {
  if (!uid) return null;
  let w = null;
  try { w = JSON.parse(storage.getItem(key(uid)) || 'null'); } catch { w = null; }
  if (!w || typeof w.message !== 'string' || !Number.isFinite(w.sentAt)) {
    clearWait(uid, storage);
    return null;
  }
  if (now - w.sentAt > MARKER_TTL_MS || now < w.sentAt - FRESH_MARGIN_MS) {
    clearWait(uid, storage);
    return null;
  }
  return w;
}

export function clearWait(uid, storage = window.sessionStorage) {
  try { storage.removeItem(key(uid)); } catch { /* */ }
}

function tsMs(ts) {
  if (!ts) return NaN;
  const s = String(ts);
  return new Date(/[Zz]$|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`).getTime();
}

/** The saved reply for THIS wait: same exact message, a non-empty response, saved from ~the send on. */
export function findReply(historyMessages, wait) {
  if (!wait) return null;
  const hits = (historyMessages || []).filter((m) => m && m.message === wait.message
    && String(m.response || '').trim() && tsMs(m.timestamp) >= wait.sentAt - FRESH_MARGIN_MS);
  return hits.length ? hits[hits.length - 1] : null;
}

/** Delay before the next history check: 5 s for the first 2 minutes, then 10 s; null once the wait is over. */
export function nextDelay(elapsedMs) {
  if (elapsedMs >= WAIT_MAX_MS) return null;
  return elapsedMs < 2 * 60 * 1000 ? 5000 : 10000;
}

/** fetch with its own timeout, also aborted when `signal` (the component's) aborts. Rejects at the timeout even if
 *  the fetcher ignores the abort (authFetch first awaits the Firebase token, which no signal can cancel). */
export async function fetchWithTimeout(fetcher, url, { signal, timeoutMs = REQUEST_TIMEOUT_MS } = {}) {
  const ctrl = new AbortController();
  const onAbort = () => ctrl.abort();
  if (signal) {
    if (signal.aborted) ctrl.abort();
    else signal.addEventListener('abort', onAbort);
  }
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => { ctrl.abort(); reject(new Error('history-timeout')); }, timeoutMs);
  });
  try {
    return await Promise.race([fetcher(url, { signal: ctrl.signal, cache: 'no-store' }), timeout]);
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onAbort);
  }
}
